import { Injectable } from '@angular/core';
import { Affected } from '@ess-front/shared/models';
import { Day } from '@app/utils/models/itinerary/day.model';
import { RouteDetails } from '@app/utils/models/itinerary/route-details.model';
import { Trip } from '@app/utils/models/itinerary/trip.model';
import { TravelState } from '@app/utils/models/store/travel-state.model';
import { StaticText } from '@app/utils/models/static-text.model';
import { StoreConfigService } from './store-config.service';
import { LoadingState } from '@app/utils/models/store/loading-state.model';
import { ItineraryState } from '@app/utils/models/store/itinerary-state.model';
import { ItineraryReducerService } from './reducers/itinerary-reducer.service';
import { ItinerarySelectorsService } from './selectors/itinerary-selectors.service';
import { Experience } from '@app/utils/models/itinerary/experience.model';
import { LOADING_MESSAGES } from '@app/utils/models/loading-messages';

@Injectable({
  providedIn: 'root',
})
export class StoreService extends StoreConfigService {
  constructor(
    itinerarySelectorService: ItinerarySelectorsService,
    private itineraryReducerService: ItineraryReducerService,
  ) {
    super(itinerarySelectorService);
  }

  public getState(): TravelState {
    return this.store$.getValue();
  }

  public getItineraryState(): ItineraryState {
    return this.getState().itineraryState;
  }

  revertState(): void {
    if (this.previousState) {
      this.setItineraryState(this.previousState);
    }
  }

  cleanItineraryState(): void {
    this.setInitialItineraryState();
  }

  updateExperience(experienceId: number, newExperience: Experience): void {
    const oldState = this.getItineraryState();
    const newState = this.itineraryReducerService.updateExperience(oldState, experienceId, newExperience);
    this.setItineraryState(newState);
  }

  deleteExperience(experienceId: number): void {
    const oldState = this.getItineraryState();
    const newState = this.itineraryReducerService.deleteExperience(oldState, experienceId);
    this.setItineraryState(newState);
  }

  syncAffectedExperiences(affectedExperiences: Affected<Experience>): void {
    const oldState = this.getItineraryState();
    const newState = this.itineraryReducerService.syncAffectedExperiences(oldState, affectedExperiences);
    this.setItineraryState(newState);
  }

  setRouteDetails(routeDetails: RouteDetails): void {
    this.setItineraryState({ routeDetails });
  }

  setTrip(trip: Trip): void {
    this.setItineraryState({
      trip,
      isSampleTrip: trip.isSample,
      isAmex: trip.isAmex,
    });
  }

  setQuote(quote: string): void {
    const itineraryState = this.getState().itineraryState;
    if (itineraryState.trip) {
      const trip: Trip = itineraryState.trip;
      trip.totalPrice = quote;
      this.setItineraryState({ ...itineraryState, trip });
    }
  }

  setStaticTexts(staticTexts: StaticText): void {
    this.setItineraryState({ staticTexts });
  }

  setActiveDay(activeDay: number): void {
    const numberOfDays = this.getState().itineraryState.trip?.numberOfDays;
    if (numberOfDays && activeDay <= numberOfDays) {
      this.setItineraryState({ activeDay });
    }
  }
  setDays(days: Day[]): void {
    this.setItineraryState({ days });
  }

  setIsLoading(isLoading: boolean, messageKey: string = LOADING_MESSAGES.default): void {
    this.setLoadingState({
      isLoading,
      messageKey: messageKey,
    });
  }

  setLoadingScreenColor(screenColor: 'white' | 'black'): void {
    this.setLoadingState({ screenColor });
  }

  setStateLoading(loadingState: LoadingState): void {
    const currentState = this.store$.getValue();
    this.store$.next({ ...currentState, loadingState });
  }
}
